import { Hit } from 'instantsearch.js';
import { FC } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { Text } from '~/shared/components';
import type { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { EventIdentifier } from '~/shared/hooks/useEvents';
import { getListName } from '~/shared/hooks/useEvents/helpers';
import { useTranslation } from '~/shared/utils/translation';
import { usePage } from '~/templates/pages';
import { useProductHandler } from '../../../../services/search/useProductHandler';
import ProductHit from './ProductHit';
import styles from './ProductList.module.scss';
import { PromotionCard as PromotionHit } from '~/shared/components/PromotionCard/PromotionCard';
import { TPromotionCard } from '~/shared/components/PromotionCard/PromotionCard.definition';
import { AlgoliaHitType } from '../../PLPModule.definitions';

interface ProductListProps {
    listId: EventIdentifier;
    hits: Hit<TProductDetails>[];
    itemsPerPage?: number;
}

export const ProductList: FC<ProductListProps> = ({ hits, listId }) => {
    const { handleFavourite, isFavorite } = useWishlist();
    const getProductHandler = useProductHandler();
    const { translate } = useTranslation();
    const { breadcrumb } = usePage();
    const { status: searchStatus } = useInstantSearch();

    const hasNoResults = hits.length === 0 && searchStatus === 'idle';

    const trackingListName = breadcrumb ? getListName(breadcrumb) : '';

    // Type guard to help TypeScript infer TPromotionCard based on hit.type
    function isPromotionCard(hit: Hit<TProductDetails> | TPromotionCard): hit is TPromotionCard {
        return 'type' in hit && hit.type === AlgoliaHitType.PROMOTIONAL_CONTENT;
    }

    return (
        <div className={styles.productList}>
            <div className={styles.grid}>
                {hits.map((hit, index) =>
                    !isPromotionCard(hit) ? (
                        <ProductHit
                            key={hit.id}
                            hit={hit}
                            listId={listId}
                            listName={trackingListName}
                            position={index}
                            isFavourite={isFavorite}
                            onFavourite={handleFavourite}
                            onBuyButtonClick={getProductHandler}
                        />
                    ) : (
                        <PromotionHit
                            key={hit.objectID}
                            {...hit}
                            listId={listId}
                            listName={trackingListName}
                        />
                    ),
                )}
            </div>
            {hasNoResults && (
                <Text variant="navigationHeader">{translate('search.noResults')}</Text>
            )}
        </div>
    );
};
